import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import { ledgerAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiBook, FiPlus, FiEdit2, FiTrash2, FiArchive, FiRotateCcw, FiEye, FiEyeOff, FiExternalLink, FiMoreVertical } from 'react-icons/fi';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

/**
 * Check if ledger has usage (balance or is older than 24 hours)
 * Ledgers with usage should have restricted actions for safety
 */
const hasLedgerUsage = (ledger) => {
  const balance = parseFloat(ledger.balance) || 0;
  if (balance !== 0) return true;

  // Check if ledger is older than 24 hours (likely has some history)
  const createdAt = new Date(ledger.created_at);
  const ageHours = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
  return ageHours > 24;
};

const Ledgers = () => {
  const { t, toneEnabled } = useTone();
  const navigate = useNavigate();
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ledger_type: 'general',
    color: '#4A90E2',
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchLedgers();
  }, [showArchived]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const fetchLedgers = async () => {
    try {
      const response = await ledgerAPI.getAll({ includeArchived: showArchived });
      setLedgers(response.data.data.ledgers || []);
      setArchivedCount(response.data.data.archivedCount || 0);
    } catch (error) {
      toast.error('Failed to load ledgers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await ledgerAPI.update(editingId, formData);
        toast.success('Ledger updated');
      } else {
        await ledgerAPI.create(formData);
        toast.success('Ledger created');
      }
      setShowModal(false);
      resetForm();
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (ledger) => {
    setFormData({
      name: ledger.name,
      description: ledger.description,
      ledger_type: ledger.ledger_type,
      color: ledger.color,
    });
    setEditingId(ledger.id);
    setShowModal(true);
  };

  const handleArchive = async (id) => {
    try {
      await ledgerAPI.archive(id);
      toast.success('Ledger archived');
      fetchLedgers();
    } catch (error) {
      toast.error('Failed to archive ledger');
    }
  };

  const handleUnarchive = async (id) => {
    try {
      await ledgerAPI.unarchive(id);
      toast.success('Ledger restored');
      fetchLedgers();
    } catch (error) {
      toast.error('Failed to restore ledger');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await ledgerAPI.delete(deleteTarget);
      toast.success('Ledger deleted');
      fetchLedgers();
    } catch (error) {
      if (error.response?.status === 409) {
        const data = error.response.data.data;
        toast.error(
          `Cannot delete: ${data.transactionCount} transactions, ${data.reminderCount} reminders, ${data.transferCount} transfers. Archive it instead.`
        );
      } else {
        toast.error(error.response?.data?.message || 'Failed to delete ledger');
      }
    } finally {
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      ledger_type: 'general',
      color: '#4A90E2',
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  const activeLedgers = ledgers.filter(l => !l.is_archived);
  const archivedLedgers = ledgers.filter(l => l.is_archived);

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><FiBook style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('ledger.title')}</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            {archivedCount > 0 && (
              <button
                className="btn btn-secondary"
                onClick={() => setShowArchived(!showArchived)}
              >
                {showArchived ? <FiEyeOff style={{ marginRight: 4 }} /> : <FiEye style={{ marginRight: 4 }} />}
                {showArchived ? 'Hide' : 'Show'} Archived ({archivedCount})
              </button>
            )}
            <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {t('ledger.add')}
            </button>
          </div>
        </div>

        {activeLedgers.length === 0 && !showArchived ? (
          <EmptyState
            icon={<FiBook />}
            title={t('ledger.no_ledgers')}
            message={t('ledger.create_first')}
            actionLabel={t('ledger.add')}
            onAction={() => { resetForm(); setShowModal(true); }}
          />
        ) : (
          <>
            <div className="grid grid-3">
              {activeLedgers.map((ledger) => {
                const hasUsage = hasLedgerUsage(ledger);
                const isMenuOpen = openMenuId === ledger.id;

                return (
                  <div key={ledger.id} className="card ledger-card" style={{ borderLeftColor: ledger.color }}>
                    <div className="budget-header">
                      <h3 style={{ cursor: 'pointer' }} onClick={() => navigate(`/ledgers/${ledger.id}`)}>{ledger.name}</h3>
                      <div className="budget-actions">
                        {/* View button - always visible */}
                        <button className="btn-icon" onClick={() => navigate(`/ledgers/${ledger.id}`)} title={toneEnabled ? 'Dekho' : 'View Details'}>
                          <FiExternalLink />
                        </button>

                        {/* If ledger has usage, show 3-dot menu for Edit/Archive */}
                        {hasUsage ? (
                          <div className="ledger-actions-menu" ref={isMenuOpen ? menuRef : null}>
                            <button
                              className="btn-icon"
                              onClick={() => setOpenMenuId(isMenuOpen ? null : ledger.id)}
                              title={toneEnabled ? 'Aur options' : 'More options'}
                            >
                              <FiMoreVertical />
                            </button>
                            {isMenuOpen && (
                              <div className="ledger-dropdown-menu">
                                <button onClick={() => { handleEdit(ledger); setOpenMenuId(null); }}>
                                  <FiEdit2 size={14} />
                                  <span>{t('action.edit')}</span>
                                </button>
                                <button onClick={() => { handleArchive(ledger.id); setOpenMenuId(null); }}>
                                  <FiArchive size={14} />
                                  <span>{t('ledger.archive')}</span>
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* No usage - show all buttons directly */
                          <>
                            <button className="btn-icon" onClick={() => handleEdit(ledger)} title={t('action.edit')}>
                              <FiEdit2 />
                            </button>
                            <button className="btn-icon" onClick={() => handleArchive(ledger.id)} title={t('ledger.archive')}>
                              <FiArchive />
                            </button>
                            <button className="btn-icon danger" onClick={() => setDeleteTarget(ledger.id)} title={t('action.delete')}>
                              <FiTrash2 />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <p>{ledger.description}</p>
                    <div className="ledger-info">
                      <span className="badge">{ledger.ledger_type}</span>
                      <div className="balance-info">
                        <span>{t('ledger.balance')}: </span>
                        <strong>{formatAmount(ledger.balance)}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {showArchived && archivedLedgers.length > 0 && (
              <>
                <h2 style={{ marginTop: 24, marginBottom: 12, color: '#7f8c8d' }}>
                  <FiArchive style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Archived
                </h2>
                <div className="grid grid-3">
                  {archivedLedgers.map((ledger) => (
                    <div key={ledger.id} className="card ledger-card" style={{ borderLeftColor: '#bdc3c7', opacity: 0.7 }}>
                      <div className="budget-header">
                        <h3 style={{ cursor: 'pointer' }} onClick={() => navigate(`/ledgers/${ledger.id}`)}>{ledger.name}</h3>
                        <div className="budget-actions">
                          <button className="btn-icon" onClick={() => navigate(`/ledgers/${ledger.id}`)} title={toneEnabled ? 'Dekho' : 'View Details'}>
                            <FiExternalLink />
                          </button>
                          <button className="btn-icon" onClick={() => handleUnarchive(ledger.id)} title={t('ledger.unarchive')}>
                            <FiRotateCcw />
                          </button>
                        </div>
                      </div>
                      <p>{ledger.description}</p>
                      <div className="ledger-info">
                        <span className="badge" style={{ background: '#bdc3c7' }}>archived</span>
                        <div className="balance-info">
                          <span>{t('ledger.balance')}: </span>
                          <strong>{formatAmount(ledger.balance)}</strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {showModal && (
          <div className="modal" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingId ? t('ledger.edit') : t('ledger.add')}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('ledger.name')}</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Personal, Business" required />
                </div>
                <div className="form-group">
                  <label>{t('transactions.description')}</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows="3" placeholder="What is this ledger for?" />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select name="ledger_type" value={formData.ledger_type} onChange={handleChange}>
                    <option value="general">General</option>
                    <option value="home">Home</option>
                    <option value="salary">Salary</option>
                    <option value="business">Business</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <input type="color" name="color" value={formData.color} onChange={handleChange} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingId ? t('action.update') : t('action.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={`${t('action.delete')} ${t('ledger.title')}`}
            message={t('ledger.delete_confirm')}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default Ledgers;
